import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";
import { Edit3, Trash2, PlusCircle, LogOut, Mail } from "lucide-react";
import ChangePasswordCard from "../../components/admin/ChangePasswordCard";

function statusColor(s) {
    return s === "published" ? "text-[var(--accent-indigo)]" : "text-[var(--accent-mercury)]";
}

function formatDate(iso) {
    if (!iso) return "—";
    try {
        return new Date(iso).toLocaleDateString(undefined, { year: "numeric", month: "short", day: "2-digit" });
    } catch {
        return iso;
    }
}

export default function AdminDashboard() {
    const { user, logout } = useAuth();
    const [posts, setPosts] = useState([]);
    const [inquiries, setInquiries] = useState([]);
    const [tab, setTab] = useState("posts");
    const [typeFilter, setTypeFilter] = useState("all");
    const [loading, setLoading] = useState(true);
    const nav = useNavigate();

    const load = async () => {
        setLoading(true);
        try {
            const [pRes, iRes] = await Promise.all([
                api.get("/admin/posts"),
                api.get("/admin/inquiries"),
            ]);
            setPosts(pRes.data || []);
            setInquiries(iRes.data || []);
        } catch (e) {
            toast.error("Failed to load data");
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        load();
    }, []);

    const onDelete = async (id) => {
        if (!confirm("Delete this post? This cannot be undone.")) return;
        try {
            await api.delete(`/posts/${id}`);
            toast.success("Post deleted");
            setPosts((p) => p.filter((x) => x.id !== id));
        } catch {
            toast.error("Delete failed");
        }
    };

    const onLogout = async () => {
        await logout();
        nav("/admin/login");
    };

    const filtered = typeFilter === "all" ? posts : posts.filter((p) => p.type === typeFilter);

    return (
        <div className="min-h-screen bg-[#050510] text-white">
            <header className="glass-nav fixed top-0 inset-x-0 z-30 py-3">
                <div className="max-w-[1440px] mx-auto px-6 md:px-10 flex items-center justify-between">
                    <div className="flex items-center gap-3">
                        <div className="w-2 h-2 bg-[var(--accent-indigo)]" />
                        <div className="font-serif-display text-xl">Skovomax · Admin</div>
                    </div>
                    <div className="flex items-center gap-4">
                        <div className="font-mono-tech text-[11px] tracking-[0.18em] uppercase text-white/55">
                            {user?.email}
                        </div>
                        <Link to="/" className="tech-label hover:text-white">Site</Link>
                        <ChangePasswordCard />
                        <button onClick={onLogout} className="btn-secondary !py-2 !px-4" data-testid="admin-logout">
                            <LogOut size={14} /> Logout
                        </button>
                    </div>
                </div>
            </header>

            <main className="pt-24 pb-20 max-w-[1440px] mx-auto px-6 md:px-10">
                <div className="flex flex-wrap items-center justify-between gap-6 mb-8">
                    <div>
                        <div className="overline mb-3">Operations</div>
                        <h1 className="font-serif-display text-5xl md:text-6xl leading-none">Content Control</h1>
                    </div>
                    <div className="flex gap-3">
                        <Link to="/admin/content" className="chip" data-testid="nav-content-editor">
                            Site Content
                        </Link>
                        <button onClick={() => setTab("posts")} className={`chip ${tab === "posts" ? "chip-active" : ""}`} data-testid="tab-posts">
                            Posts · {posts.length}
                        </button>
                        <button onClick={() => setTab("inquiries")} className={`chip ${tab === "inquiries" ? "chip-active" : ""}`} data-testid="tab-inquiries">
                            Inquiries · {inquiries.length}
                        </button>
                    </div>
                </div>

                {tab === "posts" && (
                    <>
                        <div className="flex flex-wrap items-center justify-between gap-4 mb-6">
                            <div className="flex flex-wrap gap-3">
                                {["all", "blog", "news", "event"].map((t) => (
                                    <button
                                        key={t}
                                        onClick={() => setTypeFilter(t)}
                                        className={`chip ${typeFilter === t ? "chip-active" : ""}`}
                                        data-testid={`admin-filter-${t}`}
                                    >
                                        {t}
                                    </button>
                                ))}
                            </div>
                            <Link to="/admin/posts/new" className="btn-primary" data-testid="admin-new-post">
                                <PlusCircle size={14} /> New Post
                            </Link>
                        </div>

                        <div className="border border-white/10">
                            <div className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 font-mono-tech text-[11px] tracking-[0.18em] uppercase text-white/50">
                                <div className="col-span-5">Title</div>
                                <div className="col-span-2">Type</div>
                                <div className="col-span-2">Status</div>
                                <div className="col-span-2">Updated</div>
                                <div className="col-span-1 text-right">Actions</div>
                            </div>
                            {loading && <div className="px-6 py-6 tech-label">Loading…</div>}
                            {!loading && filtered.length === 0 && (
                                <div className="px-6 py-8 tech-label">No posts.</div>
                            )}
                            {filtered.map((p) => (
                                <div key={p.id} className="grid grid-cols-12 gap-4 px-6 py-4 border-b border-white/10 items-center hover:bg-white/[0.02]">
                                    <div className="col-span-5">
                                        <div className="font-serif-display text-lg leading-tight">{p.title}</div>
                                        <div className="text-xs text-white/40 mt-1">/{p.slug}</div>
                                    </div>
                                    <div className="col-span-2 font-mono-tech text-xs uppercase tracking-widest text-white/60">{p.type}</div>
                                    <div className={`col-span-2 font-mono-tech text-xs uppercase tracking-widest ${statusColor(p.status)}`}>{p.status}</div>
                                    <div className="col-span-2 font-mono-tech text-xs text-white/50">{formatDate(p.updated_at)}</div>
                                    <div className="col-span-1 flex justify-end gap-3">
                                        <Link
                                            to={`/admin/posts/${p.id}/edit`}
                                            className="text-white/60 hover:text-[var(--accent-indigo)]"
                                            data-testid={`admin-edit-${p.id}`}
                                        >
                                            <Edit3 size={16} />
                                        </Link>
                                        <button
                                            onClick={() => onDelete(p.id)}
                                            className="text-white/60 hover:text-red-400"
                                            data-testid={`admin-delete-${p.id}`}
                                        >
                                            <Trash2 size={16} />
                                        </button>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}

                {tab === "inquiries" && (
                    <div className="border border-white/10">
                        {loading && <div className="px-6 py-6 tech-label">Loading…</div>}
                        {!loading && inquiries.length === 0 && (
                            <div className="px-6 py-8 tech-label">No inquiries yet.</div>
                        )}
                        {inquiries.map((q) => (
                            <div key={q.id} className="px-6 py-6 border-b border-white/10 hover:bg-white/[0.02]">
                                <div className="flex items-center justify-between mb-2">
                                    <div className="flex items-center gap-3">
                                        <Mail size={14} className="text-[var(--accent-indigo)]" />
                                        <div className="font-serif-display text-xl">{q.subject}</div>
                                    </div>
                                    <div className="font-mono-tech text-xs text-white/50">{formatDate(q.created_at)}</div>
                                </div>
                                <div className="text-sm text-white/60 mb-2">
                                    <span className="text-white">{q.name}</span> · {q.email}
                                    {q.company && <> · {q.company}</>}
                                    {q.interest && <> · <span className="text-[var(--accent-mercury)]">{q.interest}</span></>}
                                </div>
                                <div className="text-white/75 whitespace-pre-wrap">{q.message}</div>
                            </div>
                        ))}
                    </div>
                )}
            </main>
        </div>
    );
}
